import { useEffect, useState } from "react";
import {
  ApiError,
  CustomModelInput,
  ModelInfo,
  ModelSelection,
  Preferences,
  PreferencesUpdate,
  fetchModels,
  fetchPreferences,
  updatePreferences,
} from "../lib/api";

interface ModelSettingsProps {
  token: string;
  onClose: () => void;
  onAuthError: () => void;
}

type SaveState = "idle" | "saving" | "saved" | "error";
type Role = "diagnostic" | "classifier";

const EMPTY_CUSTOM_FORM: CustomModelInput = {
  base_url: "",
  model_name: "",
  api_key: "",
  display_name: "",
};

export default function ModelSettings({ token, onClose, onAuthError }: ModelSettingsProps) {
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([fetchModels(token), fetchPreferences(token)])
      .then(([modelsResp, prefsResp]) => {
        setModels(modelsResp.models);
        setPrefs(prefsResp);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          onAuthError();
          return;
        }
        setError(err instanceof Error ? err.message : "加载失败");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function applyUpdate(update: PreferencesUpdate): Promise<Preferences> {
    const updated = await updatePreferences(token, update);
    setPrefs(updated);
    return updated;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">模型设置</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="关闭">
            ✕
          </button>
        </div>

        {loading ? (
          <p className="mt-4 text-sm text-slate-400">加载中…</p>
        ) : error ? (
          <p className="mt-4 text-sm text-red-600">⚠️ {error}</p>
        ) : (
          prefs && (
            <div className="mt-4 space-y-5">
              <RoleSection
                role="diagnostic"
                label="主诊断模型"
                hint="用于回答问题、查数据/日志/代码"
                selection={prefs.diagnostic}
                options={models}
                onApply={applyUpdate}
                onAuthError={onAuthError}
              />
              <RoleSection
                role="classifier"
                label="意图分类模型"
                hint="用于判断问题类型，追求快速轻量"
                selection={prefs.classifier}
                options={models}
                onApply={applyUpdate}
                onAuthError={onAuthError}
              />
            </div>
          )
        )}
      </div>
    </div>
  );
}

function RoleSection({
  role,
  label,
  hint,
  selection,
  options,
  onApply,
  onAuthError,
}: {
  role: Role;
  label: string;
  hint: string;
  selection: ModelSelection;
  options: ModelInfo[];
  onApply: (update: PreferencesUpdate) => Promise<Preferences>;
  onAuthError: () => void;
}) {
  const [mode, setMode] = useState<"catalog" | "custom">(selection.source);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [customForm, setCustomForm] = useState<CustomModelInput>(
    selection.source === "custom" && selection.custom
      ? { ...EMPTY_CUSTOM_FORM, base_url: selection.custom.base_url, model_name: selection.custom.model_name, display_name: selection.custom.display_name }
      : EMPTY_CUSTOM_FORM,
  );
  const [customError, setCustomError] = useState<string | null>(null);
  const modelKeyField = role === "diagnostic" ? "diagnostic_model" : "classifier_model";
  const customField = role === "diagnostic" ? "diagnostic_custom" : "classifier_custom";

  async function handleCatalogChange(value: string) {
    setSaveState("saving");
    try {
      await onApply({ [modelKeyField]: value });
      setMode("catalog");
      setSaveState("saved");
      setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch (err) {
      setSaveState("error");
      if (err instanceof ApiError && err.status === 401) onAuthError();
    }
  }

  async function handleCustomSave() {
    if (!customForm.base_url.trim() || !customForm.model_name.trim() || !customForm.api_key.trim()) {
      setCustomError("Base URL、模型名、API Key 均为必填");
      return;
    }
    setCustomError(null);
    setSaveState("saving");
    try {
      await onApply({ [customField]: customForm });
      setSaveState("saved");
      setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch (err) {
      setSaveState("error");
      if (err instanceof ApiError && err.status === 401) {
        onAuthError();
        return;
      }
      setCustomError(err instanceof Error ? err.message : "连接测试失败");
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <p className="text-xs text-slate-400">{hint}</p>

      <div className="mt-1.5 flex gap-1 rounded-lg bg-slate-100 p-0.5 text-xs">
        <button
          type="button"
          onClick={() => setMode("catalog")}
          className={`flex-1 rounded-md py-1.5 font-medium transition ${
            mode === "catalog" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
          }`}
        >
          目录模型
        </button>
        <button
          type="button"
          onClick={() => setMode("custom")}
          className={`flex-1 rounded-md py-1.5 font-medium transition ${
            mode === "custom" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
          }`}
        >
          自定义连接
        </button>
      </div>

      {mode === "catalog" ? (
        <select
          value={selection.source === "catalog" ? selection.model_key ?? "" : ""}
          onChange={(e) => handleCatalogChange(e.target.value)}
          className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
        >
          {options.map((m) => (
            <option key={m.key} value={m.key}>
              {m.display_name}
            </option>
          ))}
        </select>
      ) : (
        <div className="mt-2 space-y-2">
          {selection.source === "custom" && selection.custom && (
            <p className="text-xs text-slate-400">
              当前已保存：{selection.custom.display_name || selection.custom.model_name}（密钥 {selection.custom.api_key_hint}）
            </p>
          )}
          <input
            type="text"
            placeholder="Base URL（必须为 https）"
            value={customForm.base_url}
            onChange={(e) => setCustomForm({ ...customForm, base_url: e.target.value })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
          />
          <input
            type="text"
            placeholder="模型名（如 gpt-4o-mini）"
            value={customForm.model_name}
            onChange={(e) => setCustomForm({ ...customForm, model_name: e.target.value })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
          />
          <input
            type="password"
            placeholder="API Key（每次保存都需重新输入）"
            value={customForm.api_key}
            onChange={(e) => setCustomForm({ ...customForm, api_key: e.target.value })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
          />
          <input
            type="text"
            placeholder="备注名（可选）"
            value={customForm.display_name}
            onChange={(e) => setCustomForm({ ...customForm, display_name: e.target.value })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
          />
          <button
            type="button"
            onClick={handleCustomSave}
            disabled={saveState === "saving"}
            className="w-full rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {saveState === "saving" ? "测试连接并保存中…" : "保存并测试连接"}
          </button>
          {customError && <p className="text-xs text-red-600">⚠️ {customError}</p>}
        </div>
      )}

      <p className="mt-1 text-xs text-slate-400">
        {saveState === "saved" && "✓ 已保存，下一条消息起生效"}
      </p>
    </div>
  );
}
